import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { LoginCallback as OktaLoginCallback } from '@okta/okta-react';

import { AppWrapper } from '../handlers/cmc/app';

import { LoginWrapper, SecurityWrapper, } from '../handlers/login/login';
import { SAMLLogin, SAMLLoginCallback } from '../handlers/login/saml';
import { BRCLoginCallback, BRCLoginErrorback } from '../handlers/login/bricata';

import Forgot from '../handlers/login/forgot';
import Cluster from '../handlers/cluster';
import Dashboard from '../handlers/dashboard/main';

// Sensor Route Handlers
import Sensors from '../handlers/sensors/';
import SensorHealth from '../handlers/sensors/health';
import SensorAdd from '../handlers/sensors/add';

// View Route Handlers
import Views from '../handlers/views/';
import Metadata from '../handlers/views/metadata/main';
import IncidentResponse from '../handlers/views/incidentresponse/';
import Incident from '../handlers/views/incidentresponse/incident';
import IncidentCreate from '../handlers/views/incidentresponse/create';
import PacketRequests from '../handlers/views/pcap/index';
import CreatePcap from '../handlers/views/pcap/createpcap';
import Alerts from '../handlers/views/alerts/main';
import Backtesting from '../handlers/views/backtesting/';
import BacktestingCreate from '../handlers/views/backtesting/create';

// Policy Route Handlers
import Policies from '../handlers/policies/';

// Policy - Sensor
import PolicySensor from '../handlers/policies/sensor/';
import PolicySelector from '../handlers/policies/selector';
import Exports from '../handlers/policies/sensor/exports';
import FileHash from '../handlers/policies/sensor/filehash';
import GeoIP from '../handlers/policies/sensor/geoip';
import PacketCaptureConfig from '../handlers/policies/sensor/packetcaptureconfig';
import SensorConfig from '../handlers/policies/sensor/config';
import DataTuning from '../handlers/policies/sensor/datatuning';
import UpgradeSensor from '../handlers/policies/sensor/upgrade';

// Policy - Suricata
import PolicySuricata from '../handlers/policies/suricata/';
import SuricataVars from '../handlers/policies/suricata/suricatavars';
import SuricataRules from '../handlers/policies/suricata/suricatarules/';
import SuricataEditor from '../handlers/policies/suricata/editor';
import SuricataSettings from '../handlers/policies/suricata/settings';

// Policy - Zeek
import PolicyZeek from '../handlers/policies/zeek/';
import PolicyZeekSettings from '../handlers/policies/zeek/settings';
import PolicyZeekEditor from '../handlers/policies/zeek/editor';
import Zeek from '../handlers/policies/zeek/zeek';
import ThreatIntel from '../handlers/policies/zeek/threatintel';
import TISettings from '../handlers/policies/zeek/threatintelsettings';

// System Route Handlers
import System from '../handlers/system/';

// System - Assets handlers
import Assets from '../handlers/system/assets/';
import CreateAsset from '../handlers/system/assets/createasset';

import Audit from '../handlers/system/audit/';
import CMCHealth from '../handlers/system/cmchealth';

// System - License handlers
import License from '../handlers/system/license/';
import LicenseRequest from '../handlers/system/license/request';

// System - Report handlers
import Reports from '../handlers/system/reports/';
import CreateReport from '../handlers/system/reports/createreport';

// System - Users and Roles handlers
import UsersRoles from '../handlers/system/usersroles/';
import CreateUser from '../handlers/system/usersroles/createuser';
import CreateRole from '../handlers/system/usersroles/createrole';
import Password from '../handlers/system/usersroles/password';
import PassReset from '../handlers/system/usersroles/passreset';

// System - Configuration
import ConfigContainer from '../handlers/system/configuration/container';
import Configuration from '../handlers/system/configuration/';
import ConfigurationAudit from '../handlers/system/configuration/audit';
import ConfigurationExports from '../handlers/system/configuration/exports';
import ConfigurationHealth from '../handlers/system/configuration/health';
import ConfigurationMail from '../handlers/system/configuration/mail';
import ConfigurationProxy from '../handlers/system/configuration/proxy';
import ConfigurationReports from '../handlers/system/configuration/reports';
import ConfigurationSNMP from '../handlers/system/configuration/snmp';
import ConfigurationSNMPCMC from '../handlers/system/configuration/snmp/cmc';
import ConfigurationSNMPDatanode from '../handlers/system/configuration/snmp/datanode';
import ConfigurationSNMPDatanodeConfig from '../handlers/system/configuration/snmp/datanode/config';
import ConfigurationUI from '../handlers/system/configuration/ui';
import ConfigurationVPN from '../handlers/system/configuration/vpn';
import ConfigurationSupport from '../handlers/system/configuration/support';
import ConfigurationEswd from '../handlers/system/configuration/eswd';
import ConfigurationWebConf from '../handlers/system/configuration/webconf';

import ConfigurationAuth from '../handlers/system/configuration/auth/';
import ConfigurationAuthLDAP from '../handlers/system/configuration/auth/ldap';
import ConfigurationAuthPriv from '../handlers/system/configuration/auth/priv';
import ConfigurationAuthStandard from '../handlers/system/configuration/auth/standard';
import Accesskeys, { CreateAccessKey } from '../handlers/system/configuration/auth/access_keys';

//System - configuration AWS groups and port Mirror
import ConfigurationAWS from '../handlers/system/portmirroring/aws';
import ConfigurationAWSCRED from '../handlers/system/portmirroring/aws/cred';
import ConfigurationAWSCreateGroup from '../handlers/system/portmirroring/aws/creategroup';
import ConfigurationAWSCreateFilter from '../handlers/system/portmirroring/aws/createfilter';
import ConfigurationAWSCreateTarget from '../handlers/system/portmirroring/aws/createtarget';
import ConfigurationAWSCreateSession from '../handlers/system/portmirroring/aws/createsession';
import ConfigurationAWSFilterHandler from '../handlers/system/portmirroring/aws/handler';
import ConfigurationGCP from '../handlers/system/portmirroring/gcp';
import ConfigurationGCPCRED from '../handlers/system/portmirroring/gcp/cred';
import ConfigurationGCPCreateInstanceGroup from '../handlers/system/portmirroring/gcp/createinstancegroup';
import ConfigurationGCPCreatePacketMirror from '../handlers/system/portmirroring/gcp/createpacketmirror';
import ConfigurationGCPCreateLoadBalancer from '../handlers/system/portmirroring/gcp/createloadbalancer';

// system - Anomalies
import AnomalyRules from '../handlers/system/anomalies/';
import OpenSearchAnomalies from '../handlers/system/anomalies/opensearch';
import OpenSearchAnomaliesEditor from '../handlers/system/anomalies/opensearch/editor';
import Anomalies from '../handlers/views/anomalies/main';

// System - Upgrades
import Upgrades from '../handlers/system/upgrades/';
import UpgradeCMC from '../handlers/system/upgrades/cmc';
import UpgradeDataNode from '../handlers/system/upgrades/datanode';

// Support
import Support from '../handlers/support/';
import SupportFileHash from '../handlers/support/filehash';
import SupportFilterReference from '../handlers/support/filterreference';
import SupportEULA from '../handlers/support/eula';
import Swagger from '../handlers/support/swagger';

import HashProtection from '../handlers/hashprotection';

// import routePerms from '../data/routes';
import { AuthenticatedRoute, PermRoute } from '../data/auth-service';
import Permissions from '../data/permissions';
import Portmirroring from '../handlers/system/portmirroring';

function routesWrapper () {
    // const perm = Auth.perm();
    // const perms = routePerms.reduce((a, c) => ({ ...a, [c.title]: c.perm }), {});
    const routes = (<Router>
        <SecurityWrapper>
            <HashProtection>
                <Routes>
                    <Route path="/signin" element={<LoginWrapper />} />
                    <Route path="/signin/forgot" element={<Forgot />} />
                    <Route path="/signin/callback" element={<OktaLoginCallback />} />
                    <Route path="/callback" element={<SAMLLoginCallback />} />
                    <Route path="/samlauth/auth" element={<SAMLLogin />} />
                    <Route path="/errorback" element={<LoginWrapper state={{ error: 'Single Sign-on may not available' }} />} />

                    <Route path="/oauth/callback" element={<BRCLoginCallback />} />
                    <Route path="/oauth/errorback" element={<BRCLoginErrorback />} />

                    <Route path="/users/set-password/:username/:token" element={<PassReset />} />
                    <Route path="/" element={<AuthenticatedRoute />} >
                        <Route path="" element={<AppWrapper />}>
                            <Route path="unauthorized" element={<Unauthorized />} />
                            <Route path="/" element={<PermRoute perm={{ Dashboards: [...Permissions.Dashboards] }} />}>
                                <Route index element={<Dashboard />} />
                            </Route>
                            {/* Main */}
                            <Route path="cluster" element={<PermRoute perm={{ Cluster: [...Permissions.Cluster] }} />}>
                                <Route index element={<Cluster />} />
                            </Route>
                            <Route path="dashboard" element={<PermRoute perm={{ Dashboards: [...Permissions.Dashboards] }} />}>
                                <Route index element={<Dashboard />} />
                            </Route>
                            {/* Sensors */}
                            <Route path="sensors">
                                <Route path="" element={<PermRoute perm={{ Sensors: [...Permissions.Sensors] }} />}>
                                    <Route index element={<Sensors />} />
                                </Route>
                                <Route path="add" element={<PermRoute perm={{ Sensors: ['write'] }} />}>
                                    <Route index element={<SensorAdd />} />
                                </Route>
                                <Route path="edit/:uuid" element={<PermRoute perm={{ Sensors: ['write', 'delete', 'admin'] }} />}>
                                    <Route index element={<SensorAdd />} />
                                </Route>
                                <Route path="health/:uuid" element={<PermRoute perm={{ Sensors: [...Permissions.Sensors] }} />}>
                                    <Route index element={<SensorHealth />} />
                                </Route>
                            </Route>
                            {/* View */}
                            <Route path="views">
                                <Route path="" element={<PermRoute perm={{
                                    ...Permissions.Views
                                }} />}>
                                    <Route index element={<Views />} />
                                </Route>
                                <Route path="alerts" element={<PermRoute perm={{ Alerts: [...Permissions.Views.Alerts] }} />}>
                                    <Route index element={<Alerts />} />
                                </Route>
                                <Route path="anomalies" element={<PermRoute perm={{ Anomalies: [...Permissions.Views.Anomalies] }} />}>
                                    <Route index element={<Anomalies />} />
                                </Route>
                                <Route path="metadata" element={<PermRoute perm={{ Metadata: [...Permissions.Views.Metadata] }} />}>
                                    <Route index element={<Metadata />} />
                                </Route>
                                <Route path="incident_response">
                                    <Route path="" element={<PermRoute perm={{ Incidents: [...Permissions.Views.Incidents] }} />}>
                                        <Route index element={<IncidentResponse />} />
                                    </Route>
                                    <Route path=":uuid" element={<PermRoute perm={{ Incidents: [...Permissions.Views.Incidents] }} />}>
                                        <Route index element={<Incident />} />
                                    </Route>
                                    <Route path="create" element={<PermRoute perm={{ Incidents: [...Permissions.Views.Incidents] }} />}>
                                        <Route index element={<IncidentCreate />} />
                                    </Route>
                                </Route>
                                <Route path="packet_requests">
                                    <Route path="" element={<PermRoute perm={{ Pcap: [...Permissions.Views.Pcap] }} />}>
                                        <Route index element={<PacketRequests />} />
                                    </Route>
                                    <Route path="create_pcap" element={<PermRoute perm={{ Pcap: ['read'] }} />}>
                                        <Route index element={<CreatePcap />} />
                                    </Route>
                                </Route>
                                <Route path="backtesting">
                                    <Route path="" element={<Backtesting />} perm={{ Backtesting: [...Permissions.Views.Backtesting] }} />
                                    <Route path="create" element={<BacktestingCreate />} perm={{ Backtesting: ['write'] }} />
                                    <Route path="edit/:jobName" element={<BacktestingCreate />} perm={{ Backtesting: ['write'] }} />
                                </Route>
                            </Route>
                            {/* Policies */}
                            <Route path="policies">
                                <Route path="" element={<PermRoute perm={{ ...Permissions.Policies.Sensor, ...Permissions.Policies.Suricata, ...Permissions.Policies.Zeek }} />}>
                                    <Route index element={<Policies />} />
                                </Route>
                                <Route path="sensor">
                                    <Route path="" element={<PermRoute perm={{ ...Permissions.Policies.Sensor }} />}>
                                        <Route index element={<PolicySensor />} />
                                    </Route>
                                    <Route path="data_tuning">
                                        <Route path="" element={<PermRoute perm={{ DataTuningPolicy: [...Permissions.Policies.Sensor.DataTuningPolicy] }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ DataTuningPolicy: [...Permissions.Policies.Sensor.DataTuningPolicy] }} />}>
                                            <Route index element={<DataTuning />} />
                                        </Route>
                                    </Route>
                                    <Route path="exports">
                                        <Route path="" element={<PermRoute perm={{ ExportsPolicy: [...Permissions.Policies.Sensor.ExportsPolicy] }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ ExportsPolicy: [...Permissions.Policies.Sensor.ExportsPolicy] }} />}>
                                            <Route index element={<Exports />} />
                                        </Route>
                                    </Route>
                                    <Route path="file_hash">
                                        <Route path="" element={<PermRoute perm={{ FileHashPolicy: [...Permissions.Policies.Sensor.FileHashPolicy] }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ FileHashPolicy: [...Permissions.Policies.Sensor.FileHashPolicy] }} />}>
                                            <Route index element={<FileHash />} />
                                        </Route>
                                    </Route>
                                    <Route path="geo_ip">
                                        <Route path="" element={<PermRoute perm={{ GeoIPPolicy: [...Permissions.Policies.Sensor.GeoIPPolicy] }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ GeoIPPolicy: [...Permissions.Policies.Sensor.GeoIPPolicy] }} />}>
                                            <Route index element={<GeoIP />} />
                                        </Route>
                                    </Route>
                                    <Route path="pcap">
                                        <Route path="" element={<PermRoute perm={{ PcapPolicy: [...Permissions.Policies.Sensor.PcapPolicy] }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ PcapPolicy: [...Permissions.Policies.Sensor.PcapPolicy] }} />}>
                                            <Route index element={<PacketCaptureConfig />} />
                                        </Route>
                                    </Route>
                                    <Route path="configuration">
                                        <Route path="" element={<PermRoute perm={{ ConfigPolicy: [...Permissions.Policies.Sensor.ConfigPolicy] }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ ConfigPolicy: [...Permissions.Policies.Sensor.ConfigPolicy] }} />}>
                                            <Route index element={<SensorConfig />} />
                                        </Route>
                                    </Route>
                                    <Route path="upgrade">
                                        <Route path="" element={<PermRoute perm={{ UpgradePolicy: [...Permissions.Policies.Sensor.UpgradePolicy] }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ UpgradePolicy: [...Permissions.Policies.Sensor.UpgradePolicy] }} />}>
                                            <Route index element={<UpgradeSensor />} />
                                        </Route>
                                    </Route>
                                </Route>
                                <Route path="suricata">
                                    <Route path="" element={<PermRoute perm={{ ...Permissions.Policies.Suricata }} />}>
                                        <Route index element={<PolicySuricata />} />
                                    </Route>
                                    <Route path="vars">
                                        <Route path="" element={<PermRoute perm={{ SuricataVarsPolicy: [...Permissions.Policies.Suricata.SuricataVarsPolicy] }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ SuricataVarsPolicy: [...Permissions.Policies.Suricata.SuricataVarsPolicy] }} />}>
                                            <Route index element={<SuricataVars />} />
                                        </Route>
                                    </Route>
                                    <Route path="rules">
                                        <Route path="" element={<PermRoute perm={{
                                            SuricataRulesPolicy: [...Permissions.Policies.Suricata.SuricataRulesPolicy],
                                            SuricataRules: [...Permissions.Policies.Suricata.SuricataRules],
                                        }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ SuricataRulesPolicy: [...Permissions.Policies.Suricata.SuricataRulesPolicy] }} />}>
                                            <Route index element={<SuricataRules />} />
                                        </Route>
                                        <Route path="settings" element={<PermRoute perm={{ SuricataRules: [...Permissions.Policies.Suricata.SuricataRules] }} />}>
                                            <Route index element={<SuricataSettings />} />
                                        </Route>
                                        <Route path="editor" element={<PermRoute perm={{ SuricataRulesPolicy: [...Permissions.Policies.Suricata.SuricataRulesPolicy] }} />}>
                                            <Route index element={<SuricataEditor />} />
                                        </Route>
                                        <Route path="editor/:id/:policy" element={<PermRoute perm={{ SuricataRulesPolicy: [...Permissions.Policies.Suricata.SuricataRulesPolicy] }} />}>
                                            <Route index element={<SuricataEditor />} />
                                        </Route>
                                    </Route>
                                </Route>
                                <Route path="zeek">
                                    <Route path="" element={<PermRoute perm={{ ...Permissions.Policies.Zeek }} />}>
                                        <Route index element={<PolicyZeek />} />
                                    </Route>
                                    <Route path="scripts">
                                        <Route path="" element={<PermRoute perm={{
                                            ZeekScriptsPolicy: [...Permissions.Policies.Zeek.ZeekScriptsPolicy],
                                            ZeekScripts: [...Permissions.Policies.Zeek.ZeekScripts],
                                        }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ ZeekScriptsPolicy: [...Permissions.Policies.Zeek.ZeekScriptsPolicy] }} />}>
                                            <Route index element={<Zeek />} />
                                        </Route>
                                        <Route path="settings" element={<PermRoute perm={{ ZeekScripts: [...Permissions.Policies.Zeek.ZeekScripts] }} />}>
                                            <Route index element={<PolicyZeekSettings />} />
                                        </Route>
                                        <Route path="editor" element={<PermRoute perm={{ ZeekScripts: [...Permissions.Policies.Zeek.ZeekScripts] }} />}>
                                            <Route index element={<PolicyZeekEditor />} />
                                        </Route>
                                        <Route path="editor/:name" element={<PermRoute perm={{ ZeekScripts: [...Permissions.Policies.Zeek.ZeekScripts] }} />}>
                                            <Route index element={<PolicyZeekEditor />} />
                                        </Route>
                                        <Route path="editor/:name/:author" element={<PermRoute perm={{ ZeekScriptsPolicy: [...Permissions.Policies.Zeek.ZeekScripts] }} />}>
                                            <Route index element={<PolicyZeekEditor />} />
                                        </Route>
                                    </Route>
                                    <Route path="zeek_indicators">
                                        <Route path="" element={<PermRoute perm={{
                                            ThreatIntelPolicy: [...Permissions.Policies.Zeek.ThreatIntelPolicy],
                                            ThreatIntelIndicators: [...Permissions.Policies.Zeek.ThreatIntelIndicators],
                                        }} />}>
                                            <Route index element={<PolicySelector />} />
                                        </Route>
                                        <Route path=":policy" element={<PermRoute perm={{ ThreatIntelPolicy: [...Permissions.Policies.Zeek.ThreatIntelPolicy] }} />}>
                                            <Route index element={<ThreatIntel />} />
                                        </Route>
                                        <Route path="settings" element={<PermRoute perm={{ ThreatIntelIndicators: [...Permissions.Policies.Zeek.ThreatIntelIndicators] }} />}>
                                            <Route index element={<TISettings />} />
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>
                            {/* System */}
                            <Route path="system">
                                <Route path="" element={<PermRoute nochecks={true} />}>
                                    <Route path="" element={<PermRoute perm={{ ...Permissions.System, Assets: [...Permissions.Assets] }} />}>
                                        <Route index element={<System />} />
                                    </Route>
                                    <Route path="assets">
                                        <Route path="" element={<PermRoute perm={{ Assets: [...Permissions.Assets] }} />}>
                                            <Route index element={<Assets />} />
                                        </Route>
                                        <Route path="create" element={<PermRoute perm={{ Assets: ['write'] }} />}>
                                            <Route index element={<CreateAsset />} />
                                        </Route>
                                        <Route path="update/:ip" element={<PermRoute perm={{ Assets: ['write'] }} />}>
                                            <Route index element={<CreateAsset />} />
                                        </Route>
                                    </Route>
                                    <Route path="audit" element={<PermRoute perm={{ Audit: [...Permissions.System.Audit] }} />}>
                                        <Route index element={<Audit />} />
                                    </Route>
                                    <Route path="cmc_health" element={<PermRoute perm={{ SystemHealth: [...Permissions.System.SystemHealth] }} />}>
                                        <Route index element={<CMCHealth />} />
                                    </Route>
                                    <Route path="anomaly_detection" element={<PermRoute perm={{ ...Permissions.System.AnomalyDetection }} />}>
                                        <Route path="" element={<PermRoute perm={{ ...Permissions.System.AnomalyDetection }} />}>
                                            <Route index element={<AnomalyRules />} />
                                        </Route>
                                        <Route path="opensearch" >
                                            <Route path="" element={<PermRoute perm={{ OpenSearchAnomalies: [...Permissions.System.AnomalyDetection.OpenSearchAnomalies] }} />}>
                                                <Route index element={<OpenSearchAnomalies />} />
                                            </Route>
                                            <Route path=":detector" element={<PermRoute perm={{ OpenSearchAnomalies: ['write'] }} />}>
                                                <Route index element={<OpenSearchAnomaliesEditor />} />
                                            </Route>
                                        </Route>
                                    </Route>
                                    <Route path="license">
                                        <Route path="" element={<PermRoute nochecks={true} />}>
                                            <Route path="" element={<PermRoute perm={{ License: [...Permissions.System.License] }} nochecks={true} />}>
                                                <Route index element={<License />} />
                                            </Route>
                                            <Route path="request" element={<PermRoute perm={{ License: ['write'] }} nochecks={true} />}>
                                                <Route index element={<LicenseRequest />} />
                                            </Route>
                                        </Route>
                                    </Route>
                                    <Route path="reports">
                                        <Route path="" element={<PermRoute perm={{ Reports: [...Permissions.System.Reports] }} />}>
                                            <Route index element={<Reports />} />
                                        </Route>
                                        <Route path="create" element={<PermRoute perm={{ Reports: ['admin'] }} />}>
                                            <Route index element={<CreateReport />} />
                                        </Route>
                                        <Route path="update" element={<PermRoute perm={{ Reports: ['admin'] }} />}>
                                            <Route index element={<CreateReport />} />
                                        </Route>
                                    </Route>
                                    <Route path="users_roles">
                                        <Route path="" element={<PermRoute nochecks={true} />}>
                                            <Route path="" element={<PermRoute perm={{ Users: [...Permissions.System.Users], Roles: [...Permissions.System.Roles] }} />}>
                                                <Route index element={<UsersRoles />} />
                                            </Route>
                                            <Route path="create_user" element={<PermRoute perm={{ Users: ['write', 'admin', 'override'] }} />}>
                                                <Route index element={<CreateUser />} />
                                            </Route>
                                            {/* user always has permission to update own page */}
                                            <Route path="update_user/:username" element={<CreateUser />} />
                                            <Route path="create_role" element={<PermRoute perm={{ Roles: ['write', 'admin', 'override'] }} />}>
                                                <Route index element={<CreateRole />} />
                                            </Route>
                                            <Route path="update_role/:role" element={<PermRoute perm={{ Roles: ['write'] }} />}>
                                                <Route index element={<CreateRole />} />
                                            </Route>
                                            <Route path="password/:username" element={<PermRoute nochecks={true} />}>
                                                <Route index element={<Password />} />
                                            </Route>
                                        </Route>
                                    </Route>
                                    <Route path="port_mirroring" element={<PermRoute perm={{ ...Permissions.System.Mirroring }} />}>
                                        <Route path="" element={<PermRoute perm={{ ...Permissions.System.Mirroring }} />}>
                                            <Route index element={<Portmirroring />} />
                                        </Route>
                                        <Route path="aws" >
                                            <Route path="" element={<PermRoute perm={{ AWS: [...Permissions.System.Mirroring.AWS] }} />}>
                                                <Route index element={<ConfigurationAWS />} />
                                            </Route>
                                            <Route path="aws_credentials" element={<PermRoute perm={{ AWS: [...Permissions.System.Mirroring.AWS] }} />}>
                                                <Route index element={<ConfigurationAWSCRED />} />
                                            </Route>
                                            <Route path="create_filter" element={<PermRoute perm={{ AWS: [...Permissions.System.Mirroring.AWS] }} />}>
                                                <Route index element={<ConfigurationAWSCreateFilter />} />
                                            </Route>
                                            <Route path="update_filter/:TrafficMirrorFilterId" element={<PermRoute perm={{ AWS: [...Permissions.System.Mirroring.AWS] }} />}>
                                                <Route index element={<ConfigurationAWSCreateFilter />} />
                                            </Route>
                                            <Route path="configure_filter_rule/:TrafficMirrorFilterId" element={<PermRoute perm={{ AWS: [...Permissions.System.Mirroring.AWS] }} />}>
                                                <Route index element={<ConfigurationAWSFilterHandler />} />
                                            </Route>
                                            <Route path="create_target" element={<PermRoute perm={{ AWS: [...Permissions.System.Mirroring.AWS] }} />}>
                                                <Route index element={<ConfigurationAWSCreateTarget />} />
                                            </Route>
                                            <Route path="create_session" element={<PermRoute perm={{ AWS: [...Permissions.System.Mirroring.AWS] }} />}>
                                                <Route index element={<ConfigurationAWSCreateSession />} />
                                            </Route>
                                            <Route path="update_session/:TrafficMirrorSessionId" element={<PermRoute perm={{ AWS: [...Permissions.System.Mirroring.AWS] }} />}>
                                                <Route index element={<ConfigurationAWSCreateSession />} />
                                            </Route>
                                            <Route path="create_group" element={<PermRoute perm={{ AWS: [...Permissions.System.Mirroring.AWS] }} />}>
                                                <Route index element={<ConfigurationAWSCreateGroup />} />
                                            </Route>
                                            <Route path="update_group/:id" element={<PermRoute perm={{ AWS: [...Permissions.System.Mirroring.AWS] }} />}>
                                                <Route index element={<ConfigurationAWSCreateGroup />} />
                                            </Route>
                                        </Route>
                                        <Route path="gcp" >
                                            <Route path="" element={<PermRoute perm={{ GCP: [...Permissions.System.Mirroring.GCP] }} />}>
                                                <Route index element={<ConfigurationGCP />} />
                                            </Route>
                                            <Route path="gcp_credentials" element={<PermRoute perm={{ GCP: [...Permissions.System.Mirroring.GCP] }} />}>
                                                <Route index element={<ConfigurationGCPCRED />} />
                                            </Route>
                                            <Route path="create_instance_group" element={<PermRoute perm={{ GCP: [...Permissions.System.Mirroring.GCP] }} />}>
                                                <Route index element={<ConfigurationGCPCreateInstanceGroup />} />
                                            </Route>
                                            <Route path="update_instance_group/:id" element={<PermRoute perm={{ GCP: [...Permissions.System.Mirroring.GCP] }} />}>
                                                <Route index element={<ConfigurationGCPCreateInstanceGroup />} />
                                            </Route>
                                            <Route path="create_packet_mirror" element={<PermRoute perm={{ GCP: [...Permissions.System.Mirroring.GCP] }} />}>
                                                <Route index element={<ConfigurationGCPCreatePacketMirror />} />
                                            </Route>
                                            <Route path="update_packet_mirror/:id" element={<PermRoute perm={{ GCP: [...Permissions.System.Mirroring.GCP] }} />}>
                                                <Route index element={<ConfigurationGCPCreatePacketMirror />} />
                                            </Route>
                                            <Route path="create_load_balancer" element={<PermRoute perm={{ GCP: [...Permissions.System.Mirroring.GCP] }} />}>
                                                <Route index element={<ConfigurationGCPCreateLoadBalancer />} />
                                            </Route>
                                            <Route path="update_load_balancer/:id" element={<PermRoute perm={{ GCP: [...Permissions.System.Mirroring.GCP] }} />}>
                                                <Route index element={<ConfigurationGCPCreateLoadBalancer />} />
                                            </Route>
                                        </Route>
                                    </Route>
                                    <Route path="configuration" element={<PermRoute perm={{ ...Permissions.System.Configuration }} />}>
                                        <Route path="" element={<ConfigContainer />}>
                                            <Route path="" element={<Configuration />} />
                                            <Route path="auth" element={<PermRoute perm={{
                                                AuthenticationStandard: [...Permissions.System.Configuration.AuthenticationStandard],
                                                AuthenticationPrivileged: [...Permissions.System.Configuration.AuthenticationPrivileged],
                                                AuthenticationExternal: [...Permissions.System.Configuration.AuthenticationExternal],
                                                Accesskeys: [...Permissions.AccessKeys],
                                            }} />}>
                                                <Route path="" element={<PermRoute perm={{
                                                    AuthenticationStandard: [...Permissions.System.Configuration.AuthenticationStandard],
                                                    AuthenticationPrivileged: [...Permissions.System.Configuration.AuthenticationPrivileged],
                                                    AuthenticationExternal: [...Permissions.System.Configuration.AuthenticationExternal],
                                                    Accesskeys: [...Permissions.AccessKeys],
                                                }} />}>
                                                    <Route index element={<ConfigurationAuth />} />
                                                </Route>
                                                <Route path="ldap" element={<PermRoute perm={{ AuthenticationExternal: [...Permissions.System.Configuration.AuthenticationExternal] }} />}>
                                                    <Route index element={<ConfigurationAuthLDAP />} />
                                                </Route>
                                                <Route path="priv" element={<PermRoute perm={{ AuthenticationPrivileged: [...Permissions.System.Configuration.AuthenticationPrivileged] }} />}>
                                                    <Route index element={<ConfigurationAuthPriv />} />
                                                </Route>
                                                <Route path="standard" element={<PermRoute perm={{ AuthenticationStandard: [...Permissions.System.Configuration.AuthenticationStandard] }} />}>
                                                    <Route index element={<ConfigurationAuthStandard />} />
                                                </Route>
                                                <Route path="access_keys" element={<PermRoute perm={{ AccessKeys: [...Permissions.AccessKeys] }} />}>
                                                    <Route index element={<Accesskeys />} />
                                                    <Route path="create_access_key" element={<PermRoute perm={{ AccessKeys: [...Permissions.AccessKeys] }} />}>
                                                        <Route index element={<CreateAccessKey />} />
                                                    </Route>
                                                </Route>
                                            </Route>
                                            <Route path="audit" element={<PermRoute perm={{ AuditExport: [...Permissions.System.Configuration.AuditExport] }} />}>
                                                <Route index element={<ConfigurationAudit />} />
                                            </Route>
                                            <Route path="exports" element={<PermRoute perm={{ CMCExport: [...Permissions.System.Configuration.CMCExport] }} />}>
                                                <Route index element={<ConfigurationExports />} />
                                            </Route>
                                            <Route path="health" element={<PermRoute perm={{ HealthAlerts: [...Permissions.System.Configuration.HealthAlerts] }} />}>
                                                <Route index element={<ConfigurationHealth />} />
                                            </Route>
                                            <Route path="email" element={<PermRoute perm={{ Mail: [...Permissions.System.Configuration.Mail] }} />}>
                                                <Route index element={<ConfigurationMail />} />
                                            </Route>
                                            <Route path="proxy" element={<PermRoute perm={{ Proxy: [...Permissions.System.Configuration.Proxy] }} />}>
                                                <Route index element={<ConfigurationProxy />} />
                                            </Route>
                                            <Route path="reports" element={<PermRoute perm={{ ReportsConfig: [...Permissions.System.Configuration.ReportsConfig] }} />}>
                                                <Route index element={<ConfigurationReports />} />
                                            </Route>
                                            <Route path="snmp" element={<PermRoute perm={{ SNMP: [...Permissions.System.Configuration.SNMP] }} />}>
                                                <Route index element={<ConfigurationSNMP />} />
                                                <Route path="cmc" element={<PermRoute perm={{ SNMP: [...Permissions.System.Configuration.SNMP] }} />}>
                                                    <Route index element={<ConfigurationSNMPCMC />} />
                                                </Route>
                                                <Route path="datanode" element={<PermRoute perm={{ Cluster: [...Permissions.Cluster] }} />}>
                                                    <Route index element={<ConfigurationSNMPDatanode />} />
                                                    <Route path=":config" element={<ConfigurationSNMPDatanodeConfig />} />
                                                </Route>
                                            </Route>
                                            <Route path="ui" element={<PermRoute perm={{ UI: [...Permissions.System.Configuration.UI] }} />}>
                                                <Route index element={<ConfigurationUI />} />
                                            </Route>
                                            <Route path="vpn" element={<PermRoute perm={{ VPN: [...Permissions.System.Configuration.VPN] }} />}>
                                                <Route index element={<ConfigurationVPN />} />
                                            </Route>
                                            <Route path="support" element={<PermRoute perm={{ Support: [...Permissions.System.Configuration.Support] }} />}>
                                                <Route index element={<ConfigurationSupport />} />
                                            </Route>
                                            <Route path="eswd" element={<PermRoute perm={{ MetadataIngest: [...Permissions.System.Configuration.MetadataIngest] }} />}>
                                                <Route index element={<ConfigurationEswd />} />
                                            </Route>
                                            <Route path="web" element={<PermRoute perm={{ WebServer: [...Permissions.System.Configuration.WebServer] }} />}>
                                                <Route index element={<ConfigurationWebConf />} />
                                            </Route>
                                        </Route>
                                    </Route>
                                    {/* Upgrades */}
                                    <Route path="upgrades">
                                        <Route path="" element={<PermRoute perm={{
                                            CMCUpgrades: [...Permissions.System.Upgrades.CMCUpgrades],
                                            DatanodeUpgrades: [...Permissions.System.Upgrades.DatanodeUpgrades],
                                        }} />}>
                                            <Route index element={<Upgrades />} />
                                        </Route>
                                        <Route path="cmc" element={<PermRoute perm={{ CMCUpgrades: [...Permissions.System.Upgrades.CMCUpgrades] }} />}>
                                            <Route index element={<UpgradeCMC />} />
                                        </Route>
                                        <Route path="datanode" element={<PermRoute perm={{ DatanodeUpgrades: [...Permissions.System.Upgrades.DatanodeUpgrades] }} />}>
                                            <Route index element={<UpgradeDataNode />} />
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>
                            {/* Support */}
                            <Route path="support" nochecks={true}>
                                <Route path="" element={<PermRoute nochecks={true} />}>
                                    <Route index element={<Support />} />
                                </Route>
                                <Route path="filehash" element={<SupportFileHash />} />
                                <Route path="filter_reference" element={<SupportFilterReference />} />
                                <Route path="eula" element={<PermRoute nochecks={true} />}>
                                    <Route index element={<SupportEULA />} />
                                </Route>
                                <Route path="api_docs" element={<PermRoute nochecks={true} />}>
                                    <Route index element={<Swagger />} />
                                </Route>
                            </Route>
                        </Route>
                    </Route>
                    <Route path="*" element={<Navigate to="/signin" />} />
                </Routes>
            </HashProtection>
        </SecurityWrapper>
    </Router>);
    return routes;
}

const Unauthorized = () => {
    console.log("Unauthorized");
    return null;
};

export default routesWrapper;
